/**
 * FinanceAssessment — FM3P Finance Assessment Authorization
 *
 * Route: /finance/assessments
 * RBAC: finance.assessment.view  (view reference, preview, runs)
 *       finance.assessment.create (authorize — institution_admin, super_admin ONLY)
 *
 * FM3P GOVERNANCE:
 *   - Preview = ZERO PERSISTENCE. Amounts shown are Finance simulations, NOT student liabilities.
 *   - Assessment is only created after explicit admin authorization (confirm gate).
 *   - No student-facing pending preview — student visibility only after canonical FM3 posting.
 *   - FM1, FM2, ENR1 routes untouched.
 *
 * Tabs:
 *   Authorize Assessment — select offering + schedule → preview → confirm gate → authorize
 *   Assessment Runs      — run history with detail panel
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  AlertCircle, CheckCircle2, ChevronDown, ChevronRight,
  ClipboardCheck, Eye, RefreshCw, Shield, Users, FileText,
  AlertTriangle, ArrowRight, Info
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/layout/Shell";
import { ResponsivePageContainer } from "../components/shared/ResponsivePageContainer";
import { CapabilityState } from "../components/shared/CapabilityState";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import {
  Alert, AlertDescription, AlertTitle
} from "../components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CAN_VIEW = ["finance_manager", "institution_admin", "super_admin"];
const CAN_AUTHORIZE = ["institution_admin", "super_admin"];

function formatCurrency(amount, iso) {
  if (!amount) return "—";
  const n = parseFloat(amount);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: iso || "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

function StatusBadge({ status }) {
  const map = {
    PENDING: "bg-amber-100 text-amber-800 border-amber-200",
    ASSESSED: "bg-green-100 text-green-800 border-green-200",
    CANCELLED: "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${map[status] || "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}

function MatchBadge({ status }) {
  const map = {
    SINGLE_MATCH: { cls: "bg-green-100 text-green-800", label: "1 Schedule" },
    MULTIPLE_MATCHES: { cls: "bg-amber-100 text-amber-800", label: "Multiple" },
    NO_PUBLISHED_SCHEDULE: { cls: "bg-red-100 text-red-800", label: "No Match" },
  };
  const { cls, label } = map[status] || { cls: "bg-gray-100 text-gray-700", label: status };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>;
}

// ─── Preview Persistence Notice ───────────────────────────────────────────────

function PreviewNotice() {
  return (
    <Alert className="border-blue-200 bg-blue-50 mb-4">
      <Info className="h-4 w-4 text-blue-600" />
      <AlertTitle className="text-blue-800 text-sm font-semibold">Finance Simulation — Not a Student Liability</AlertTitle>
      <AlertDescription className="text-blue-700 text-xs mt-1">
        This preview is a Finance calculation only. Amounts shown are NOT posted to student accounts
        until assessment is explicitly authorized and canonical FM3 posting occurs.
        Students do not see this preview.
      </AlertDescription>
    </Alert>
  );
}

// ─── Authorize Assessment Tab ─────────────────────────────────────────────────

function AuthorizeTab({ role }) {
  const [reference, setReference] = useState(null);
  const [loadingRef, setLoadingRef] = useState(false);
  const [selectedOffering, setSelectedOffering] = useState("");
  const [selectedSchedule, setSelectedSchedule] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const canAuthorize = CAN_AUTHORIZE.includes(role);

  const loadReference = useCallback(async () => {
    setLoadingRef(true);
    try {
      const { data } = await api.get("/finance/assessment/reference");
      setReference(data);
    } catch (err) {
      toast.error("Failed to load assessment reference data.");
    } finally {
      setLoadingRef(false);
    }
  }, []);

  useEffect(() => { loadReference(); }, [loadReference]);

  // Auto-select schedule if only one match
  useEffect(() => {
    if (!selectedOffering || !reference) return;
    const off = reference.offerings.find(o => o.id === selectedOffering);
    if (!off) return;
    if (off.matching_schedule_ids.length === 1) {
      setSelectedSchedule(off.matching_schedule_ids[0]);
    } else {
      setSelectedSchedule("");
    }
    setPreview(null);
    setLastRun(null);
  }, [selectedOffering, reference]);

  const runPreview = useCallback(async () => {
    if (!selectedOffering || !selectedSchedule) return;
    setLoadingPreview(true);
    setPreview(null);
    try {
      const { data } = await api.post("/finance/assessment/preview", {
        program_offering_id: selectedOffering,
        fee_schedule_id: selectedSchedule,
      });
      setPreview(data);
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail || "Preview failed.";
      toast.error(msg);
    } finally {
      setLoadingPreview(false);
    }
  }, [selectedOffering, selectedSchedule]);

  const doAuthorize = useCallback(async () => {
    if (!selectedOffering || !selectedSchedule) return;
    setAuthorizing(true);
    setShowConfirm(false);
    try {
      const { data } = await api.post("/finance/assessment/authorize", {
        program_offering_id: selectedOffering,
        fee_schedule_id: selectedSchedule,
        confirm: true,
      });
      setLastRun(data);
      setPreview(null);
      toast.success(
        `Assessment authorized. ${data.created_count} records created, ${data.skipped_count} skipped.`
      );
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "object" ? d.message : d || "Authorization failed.";
      toast.error(msg);
    } finally {
      setAuthorizing(false);
    }
  }, [selectedOffering, selectedSchedule]);

  if (loadingRef) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
        <RefreshCw className="h-4 w-4 animate-spin mr-2" /> Loading reference data…
      </div>
    );
  }

  if (!reference) return null;

  const { offerings, schedules } = reference;

  if (!offerings.length) {
    return (
      <CapabilityState
        status="NOT_CONFIGURED"
        title="No Program Offerings Available"
        description="There are no OPEN or CLOSED program offerings. Create offerings via Student Lifecycle → Program Offerings before assessing fees."
      />
    );
  }

  if (!schedules.length) {
    return (
      <CapabilityState
        status="NOT_CONFIGURED"
        title="No Published Fee Schedules"
        description="There are no PUBLISHED fee schedules. Publish a fee schedule via Finance → Fee Setup before assessing fees."
      />
    );
  }

  const selectedOfferingObj = offerings.find(o => o.id === selectedOffering);
  const availableSchedules = selectedOfferingObj
    ? schedules.filter(s => selectedOfferingObj.matching_schedule_ids.includes(s.id))
    : schedules;

  return (
    <div className="space-y-6">
      <PreviewNotice />

      {/* Step 1: Select Offering */}
      <div className="bg-white border rounded-lg p-5 space-y-4" data-testid="assessment-offering-select-section">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">1</span>
          Select Program Offering
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-scroll-container">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="text-left py-2 px-3 font-medium">Select</th>
                <th className="text-left py-2 px-3 font-medium">Program</th>
                <th className="text-left py-2 px-3 font-medium">Version</th>
                <th className="text-left py-2 px-3 font-medium">Academic Year</th>
                <th className="text-left py-2 px-3 font-medium">Mode</th>
                <th className="text-left py-2 px-3 font-medium">Enrolled</th>
                <th className="text-left py-2 px-3 font-medium">Fee Schedule</th>
              </tr>
            </thead>
            <tbody>
              {offerings.map(o => (
                <tr
                  key={o.id}
                  onClick={() => setSelectedOffering(o.id)}
                  className={`border-b cursor-pointer transition-colors ${
                    selectedOffering === o.id ? "bg-blue-50" : "hover:bg-gray-50"
                  }`}
                  data-testid={`offering-row-${o.id}`}
                >
                  <td className="py-2 px-3">
                    <input
                      type="radio"
                      checked={selectedOffering === o.id}
                      onChange={() => setSelectedOffering(o.id)}
                      className="accent-blue-600"
                    />
                  </td>
                  <td className="py-2 px-3 font-medium">{o.program_code}</td>
                  <td className="py-2 px-3 text-muted-foreground">v{o.version_no}</td>
                  <td className="py-2 px-3">{o.academic_year_code || "—"}</td>
                  <td className="py-2 px-3">
                    <Badge variant="outline" className="text-xs">{o.delivery_mode}</Badge>
                  </td>
                  <td className="py-2 px-3">
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3 text-muted-foreground" />
                      {o.enrolled_count}
                    </span>
                  </td>
                  <td className="py-2 px-3"><MatchBadge status={o.schedule_match_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Step 2: Select Schedule (only if offering selected) */}
      {selectedOffering && (
        <div className="bg-white border rounded-lg p-5 space-y-4" data-testid="assessment-schedule-select-section">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">2</span>
            Select Fee Schedule
          </h3>
          {availableSchedules.length === 0 ? (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700 text-sm">
                No PUBLISHED fee schedule matches this offering. Publish a matching schedule via Finance → Fee Setup.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-2">
              {availableSchedules.map(s => (
                <div
                  key={s.id}
                  onClick={() => setSelectedSchedule(s.id)}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedSchedule === s.id
                      ? "border-blue-400 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                  data-testid={`schedule-row-${s.id}`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      checked={selectedSchedule === s.id}
                      onChange={() => setSelectedSchedule(s.id)}
                      className="accent-blue-600"
                    />
                    <div>
                      <p className="text-sm font-medium">{s.code}</p>
                      <p className="text-xs text-muted-foreground">{s.name}</p>
                    </div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <div>{s.active_rule_count} active rule{s.active_rule_count !== 1 ? "s" : ""}</div>
                    <div>{s.currency_iso}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 3: Preview */}
      {selectedOffering && selectedSchedule && (
        <div className="bg-white border rounded-lg p-5 space-y-4" data-testid="assessment-preview-section">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">3</span>
              Assessment Preview
            </h3>
            <Button
              size="sm" variant="outline"
              onClick={runPreview}
              disabled={loadingPreview}
              data-testid="run-preview-btn"
            >
              {loadingPreview ? (
                <RefreshCw className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Eye className="h-3 w-3 mr-1" />
              )}
              {loadingPreview ? "Calculating…" : "Run Preview"}
            </Button>
          </div>

          {preview && (
            <div className="space-y-4">
              <PreviewNotice />

              {/* Enrollment summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-gray-900">{preview.enrollment_summary.enrolled_count}</div>
                  <div className="text-xs text-muted-foreground">Enrolled</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-gray-900">{preview.enrollment_summary.assessable_count}</div>
                  <div className="text-xs text-muted-foreground">Assessable</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-blue-700">{preview.applicable_rule_count}</div>
                  <div className="text-xs text-muted-foreground">Rules Applied</div>
                </div>
              </div>

              {/* Rule breakdown */}
              {preview.supported_rule_breakdown?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">Rule Breakdown</p>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">Rule</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">Type</th>
                          <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Per Student</th>
                          <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Students</th>
                          <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Rule Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.supported_rule_breakdown.map(r => (
                          <tr key={r.rule_id} className="border-b last:border-0">
                            <td className="py-2 px-3 font-medium">{r.rule_code}</td>
                            <td className="py-2 px-3"><Badge variant="outline" className="text-xs">{r.fee_type}</Badge></td>
                            <td className="py-2 px-3 text-right font-mono text-sm">
                              {formatCurrency(r.amount_per_student, preview.currency_iso)}
                            </td>
                            <td className="py-2 px-3 text-right">{r.assessable_student_count}</td>
                            <td className="py-2 px-3 text-right font-mono text-sm font-semibold">
                              {formatCurrency(r.rule_total, preview.currency_iso)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 border-t">
                        <tr>
                          <td colSpan={4} className="py-2 px-3 text-sm font-semibold text-right">Grand Total</td>
                          <td className="py-2 px-3 text-right font-mono font-bold text-blue-700">
                            {formatCurrency(preview.grand_total_assessment, preview.currency_iso)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Unsupported rules warning */}
              {preview.unsupported_rules?.length > 0 && (
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 text-xs">
                    {preview.unsupported_rules.length} rule(s) use formula types not supported in FM3P (FIXED only):
                    {" "}{preview.unsupported_rules.map(r => r.rule_code).join(", ")}.
                    These will be skipped during authorization.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step 4: Authorize Gate */}
      {preview && canAuthorize && (
        <div className="bg-white border border-blue-200 rounded-lg p-5 space-y-4" data-testid="assessment-authorize-section">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">4</span>
            Authorize Assessment
          </h3>
          <Alert className="border-amber-200 bg-amber-50">
            <Shield className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800 text-sm font-semibold">Explicit Authorization Required</AlertTitle>
            <AlertDescription className="text-amber-700 text-xs mt-1">
              Authorizing will create {preview.enrollment_summary.assessable_count} fee assessment
              record{preview.enrollment_summary.assessable_count !== 1 ? "s" : ""} totalling{" "}
              <strong>{formatCurrency(preview.grand_total_assessment, preview.currency_iso)}</strong>.
              This action is recorded in the audit trail with your identity.
            </AlertDescription>
          </Alert>

          {showConfirm ? (
            <div className="flex items-center gap-3 pt-2">
              <Button
                onClick={doAuthorize}
                disabled={authorizing}
                className="bg-blue-700 hover:bg-blue-800 text-white"
                data-testid="confirm-authorize-btn"
              >
                {authorizing ? (
                  <RefreshCw className="h-3 w-3 animate-spin mr-2" />
                ) : (
                  <ClipboardCheck className="h-4 w-4 mr-2" />
                )}
                {authorizing ? "Authorizing…" : "Confirm Authorization"}
              </Button>
              <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={authorizing}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setShowConfirm(true)}
              variant="outline"
              className="border-blue-400 text-blue-700 hover:bg-blue-50"
              data-testid="initiate-authorize-btn"
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Proceed to Authorize
            </Button>
          )}
        </div>
      )}

      {preview && !canAuthorize && (
        <Alert className="border-orange-200 bg-orange-50">
          <Shield className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-700 text-sm">
            You have view-only access to assessment previews. Authorization requires institution_admin or super_admin role.
          </AlertDescription>
        </Alert>
      )}

      {/* Last run result */}
      {lastRun && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2" data-testid="assessment-last-run-result">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <span className="text-sm font-semibold text-green-800">Assessment Authorized</span>
          </div>
          <div className="text-xs text-green-700 space-y-1">
            <div>Run ID: <span className="font-mono">{lastRun.assessment_run_id}</span></div>
            <div>Created: <strong>{lastRun.created_count}</strong> records · Skipped: {lastRun.skipped_count} · Errors: {lastRun.error_count}</div>
            <div>Total Amount: <strong>{formatCurrency(lastRun.total_assessed_amount, "INR")}</strong></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Assessment Runs Tab ──────────────────────────────────────────────────────

function RunsTab() {
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/finance/assessment/runs?page=${page}&per_page=20`);
      setRuns(data.runs || []);
      setTotal(data.total_count || 0);
    } catch (err) {
      toast.error("Failed to load assessment runs.");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  const openRunDetail = useCallback(async (run) => {
    setSelectedRun(run);
    setLoadingDetail(true);
    setRunDetail(null);
    try {
      const { data } = await api.get(`/finance/assessment/runs/${run.run_id}`);
      setRunDetail(data);
    } catch (err) {
      toast.error("Failed to load run detail.");
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
        <RefreshCw className="h-4 w-4 animate-spin mr-2" /> Loading runs…
      </div>
    );
  }

  if (!runs.length) {
    return (
      <CapabilityState
        status="NOT_CONFIGURED"
        title="No Assessment Runs"
        description="No fee assessments have been authorized yet. Use the 'Authorize Assessment' tab to create the first assessment run."
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{total} run{total !== 1 ? "s" : ""}</p>
        <Button size="sm" variant="ghost" onClick={loadRuns} data-testid="refresh-runs-btn">
          <RefreshCw className="h-3 w-3" />
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm table-scroll-container">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground">Run ID</th>
              <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground">Offering</th>
              <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground">Schedule</th>
              <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground">Records</th>
              <th className="text-right py-2.5 px-4 text-xs font-medium text-muted-foreground">Total</th>
              <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground">Authorized</th>
              <th className="py-2.5 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {runs.map(run => (
              <tr key={run.run_id} className="border-b hover:bg-gray-50 transition-colors">
                <td className="py-2.5 px-4">
                  <span className="font-mono text-xs text-muted-foreground">{run.run_id?.slice(0, 8)}…</span>
                </td>
                <td className="py-2.5 px-4">
                  {run.offering ? (
                    <div>
                      <div className="font-medium">{run.offering.program_code}</div>
                      <div className="text-xs text-muted-foreground">
                        v{run.offering.version_no} · {run.offering.academic_year_code || "—"}
                      </div>
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </td>
                <td className="py-2.5 px-4">
                  {run.schedule ? (
                    <div>
                      <div className="text-sm">{run.schedule.code}</div>
                      <div className="text-xs text-muted-foreground">{run.schedule.currency_iso}</div>
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </td>
                <td className="py-2.5 px-4">
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3 text-muted-foreground" />
                    {run.assessment_count}
                  </span>
                </td>
                <td className="py-2.5 px-4 text-right font-mono font-semibold">
                  {run.schedule ? formatCurrency(run.total_amount, run.schedule.currency_iso) : run.total_amount}
                </td>
                <td className="py-2.5 px-4 text-xs text-muted-foreground">
                  {run.authorized_at ? new Date(run.authorized_at).toLocaleString("en-IN", {
                    dateStyle: "short", timeStyle: "short"
                  }) : "—"}
                </td>
                <td className="py-2.5 px-4">
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => openRunDetail(run)}
                    data-testid={`view-run-${run.run_id?.slice(0, 8)}`}
                  >
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Run detail sheet */}
      <Sheet open={!!selectedRun} onOpenChange={open => !open && setSelectedRun(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-base">Assessment Run Detail</SheetTitle>
            {selectedRun && (
              <SheetDescription className="font-mono text-xs text-muted-foreground">
                {selectedRun.run_id}
              </SheetDescription>
            )}
          </SheetHeader>

          <div className="mt-6">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                <RefreshCw className="h-4 w-4 animate-spin mr-2" /> Loading…
              </div>
            ) : runDetail ? (
              <div className="space-y-6">
                {/* Meta */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Authorized By</p>
                    <p className="font-medium">{runDetail.authorized_by || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Authorized At</p>
                    <p className="font-medium">
                      {runDetail.authorized_at
                        ? new Date(runDetail.authorized_at).toLocaleString("en-IN")
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Records</p>
                    <p className="font-bold text-blue-700">{runDetail.assessment_count}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Amount</p>
                    <p className="font-bold">{formatCurrency(runDetail.total_amount, "INR")}</p>
                  </div>
                </div>

                <Separator />

                {/* Row table */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                    Assessment Records
                  </p>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">Student</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">Rule</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">Type</th>
                          <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Amount</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {runDetail.rows.map(r => (
                          <tr key={r.fee_assessment_id} className="border-b last:border-0">
                            <td className="py-2 px-3 font-mono text-xs">{r.student_number}</td>
                            <td className="py-2 px-3 text-xs font-medium">{r.rule_code}</td>
                            <td className="py-2 px-3">
                              <Badge variant="outline" className="text-xs">{r.fee_type}</Badge>
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-xs font-semibold">
                              {formatCurrency(r.assessed_amount, r.currency_iso)}
                            </td>
                            <td className="py-2 px-3"><StatusBadge status={r.status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// ─── Page Root ────────────────────────────────────────────────────────────────

export default function FinanceAssessment() {
  const { user } = useAuth();
  const role = user?.role || "";

  if (!CAN_VIEW.includes(role)) {
    return (
      <ResponsivePageContainer>
        <CapabilityState
          status="PERMISSION_DENIED"
          title="Access Denied"
          description="Finance Assessment requires finance_manager, institution_admin, or super_admin role."
        />
      </ResponsivePageContainer>
    );
  }

  return (
    <ResponsivePageContainer>
      <PageHeader
        title="Fee Assessment Authorization"
        subtitle="Authorize fee assessments from authoritative program enrollments and published fee schedules"
      />

      <Tabs defaultValue="authorize" className="mt-6">
        <TabsList className="mb-6">
          <TabsTrigger value="authorize" data-testid="tab-authorize">
            <ClipboardCheck className="h-4 w-4 mr-2" />
            Authorize Assessment
          </TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs">
            <FileText className="h-4 w-4 mr-2" />
            Assessment Runs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="authorize" data-testid="assessment-authorize-tab">
          <AuthorizeTab role={role} />
        </TabsContent>

        <TabsContent value="runs" data-testid="assessment-runs-tab">
          <RunsTab />
        </TabsContent>
      </Tabs>
    </ResponsivePageContainer>
  );
}
